import ReviewAudit from 'components/ReviewAudit'
import { useTranslation } from 'react-i18next'
import React, { useState, useEffect } from 'react'
import {
    Card,
    CardBody,
    Col,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
  } from "reactstrap"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import moment from "moment"
import { Link } from "react-router-dom"

import { getPlayerRatings } from '../api'

const listColumns = (globalScope) => [
  {
    dataField: "createdAt",
    text: "Timestamp",
    formatter: (cellContent, record) => {
      return (
        <p className="mb-0">{moment(record?.timestamp).format("YYYY-MM-DD HH:mm")}</p>
      )
    },
  },
  ...(!globalScope ? [{
    dataField: "action",
    text: "Action",
    formatter: (cellContent, record) => {
      return (
        <p className="mb-0">{record?.editedAt ? 'Updated' : 'Created'}</p>
      )
    },
  }] : []),
  {
    dataField: "ratedBy",
    text: "Rated by",
    formatter: (cellContent, record) => {
      return record?.sender?.[0] ? <Link to={`/accounts/players/${record?.sender?.[0]?._id}`} className="text-dark" target="_blank">
      {record?.sender?.[0]?.firstName} {record?.sender?.[0]?.lastName} #{record?.sender?.[0]?.userId}
    </Link> : '';
    },
  },
  {
    dataField: "rating",
    text: "Rating",
  },
  {
    dataField: "sportType",
    text: "Source sport",
  },
  {
    dataField: "sourceType",
    text: "Source",
    formatter: (cellContent, record) => <span>{record?.sourceType ?? ""}{record?.sourceMatchId ? ` · ${record.sourceMatchId}` : ""}</span>,
  },
  {
    dataField: "status",
    text: "Status",
    formatter: (cellContent, record) => {
      return (
        <p className="mb-0 text-capitalize">{record?.status}</p>
      )
    },
  },
]

const RatingsModal = ({
    playerId,
    actorId,
    onClose,
    viewRatingsModal,
    canViewRatings = false,
  }) => {
    const { t } = useTranslation('reviewAudit')
    const [showHistory, setShowHistory] = useState(false)
    const initialOptions = {
      page: 1,
      limit: 15,
    }
    const [options, setOptions] = useState(initialOptions)
    const [ratings, setRatings] = useState([])
    const [globalScope, setGlobalScope] = useState(false)
    const [totalRatings, setTotalRatings] = useState(0)
    const [loadState, setLoadState] = useState('idle')
    const [retry, setRetry] = useState(0)


    const pageOptions = {
      page: options?.page,
      sizePerPage: 15,
      totalSize: totalRatings,
      custom: true,
      hidePageListOnlyOnePage: true,
    }

    useEffect(() => {
      let current = true
      setRatings([])
      setTotalRatings(0)
      if (viewRatingsModal && canViewRatings) {
        setLoadState('loading')
        async function fetchRatings() {
          try {
            const { success, data } = await getPlayerRatings({ id: playerId, page: options.page, limit: options.limit });
            if (!current) return
            if (!success) { setLoadState('error'); return }
            setRatings(data?.ratings ?? [])
            setGlobalScope(data?.scope === 'global')
            setTotalRatings(data?.length ?? 0)
            setLoadState('loaded')
          } catch {
            if (current) setLoadState('error')
          }
        }
        fetchRatings();
      }
      return () => { current = false }
    }, [viewRatingsModal, canViewRatings, playerId, options, retry]);

    const closeModal = () => {
      setShowHistory(false);
      onClose();
      setOptions(initialOptions);
    }

    if (!canViewRatings) return null
    return (
      <Modal
        isOpen={viewRatingsModal}
        toggle={closeModal}
        size="lg"
      >
        <ModalHeader toggle={closeModal}>{globalScope ? 'Player reputation · Current contributions' : 'Ratings List'}</ModalHeader>
        <ModalBody>
          <details open={showHistory}><summary onClick={event => { event.preventDefault(); setShowHistory(value => !value) }}>{t('sourceHistory')}</summary>
            {showHistory && viewRatingsModal && <ReviewAudit actorId={actorId} playerId={playerId} mode="received" canView={canViewRatings} />}
          </details>
          {loadState === 'loading' && <p role="status">Loading ratings…</p>}
          {loadState === 'error' && <div role="alert">Unable to load ratings. Check your access or sign in again.
            <Button color="secondary" onClick={() => setRetry(value => value + 1)}>Retry</Button>
          </div>}
          {loadState === 'loaded' && <PaginationProvider
            pagination={paginationFactory(pageOptions)}
          >
            {({ paginationProps, paginationTableProps }) => (
              <ToolkitProvider
                keyField={globalScope ? "_id" : "id"}
                data={ratings || []}
                columns={listColumns(globalScope)}
                bootstrap4
                search
              >
                {toolkitProps => (
                  <>
                    <Row>
                      <Col xl="12">
                        <div className="table-responsive">
                          <BootstrapTable
                            responsive
                            remote
                            bordered={false}
                            striped={false}
                            classes={
                              "table table align-middle table-nowrap table-hover"
                            }
                            headerWrapperClasses={"thead-light"}
                            {...toolkitProps.baseProps}
                            onTableChange={(type, data) =>
                              setOptions({
                                ...options,
                                page: data?.page,
                              })
                            }
                            {...paginationTableProps}
                          />
                        </div>

                        {totalRatings === 0 && (
                          <Card>
                            <CardBody>
                              <p className="mb-0">There are no ratings yet</p>
                            </CardBody>
                          </Card>
                        )}
                      </Col>
                    </Row>
                    <Row className="align-items-md-center mt-30">
                      <Col className="pagination pagination-rounded justify-content-center mb-2 inner-custom-pagination">
                        <PaginationListStandalone
                          {...paginationProps}
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </ToolkitProvider>
            )}
          </PaginationProvider>}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={closeModal}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  export default RatingsModal;
